<!-- src/views/DeliveryNotesView.vue -->
<template>
  <div class="container coredeck-page" style="max-width: 1400px; margin-top: 24px">
    <!-- =====================================================
         PAGE HEADER
         ===================================================== -->
    <div class="page-header">
      <div>
        <h4 style="margin: 0">Delivery Notes</h4>

        <p class="grey-text text-darken-1" style="margin: 6px 0 0 0">
          Deliveries and internal stock movements for the active venue.
        </p>
      </div>
    </div>

    <!-- =====================================================
         NO ACTIVE VENUE
         ===================================================== -->
    <div v-if="!activeVenueId" class="card">
      <div class="card-content">
        <div class="no-venue-message">
          <i class="material-icons"> info_outline </i>

          <span> No active venue is available. </span>
        </div>
      </div>
    </div>

    <!-- =====================================================
         ACTIVE VENUE
         ===================================================== -->
    <template v-else>
      <!-- ===================================================
           ERROR
           =================================================== -->
      <div v-if="error" class="card error-card">
        <div class="card-content">
          {{ error }}
        </div>
      </div>

      <!-- ===================================================
           DELIVERY NOTES TABLE
           =================================================== -->
      <StockDeliveryNotesTable
        :notes="deliveryNotes"
        :loading="loading"
        @refresh="loadData"
      />

      <!-- ===================================================
           INTERNAL TRANSFERS TABLE
           =================================================== -->
      <StockInternalTransfersTable :transfers="internalTransfers" :loading="loading" />

      <!-- ===================================================
           DELIVERY NOTE MODAL
           =================================================== -->
      <StockDeliveryNoteModal
        ref="deliveryNoteModal"
        :venue-id="activeVenueId"
        @saved="onDeliveryNoteSaved"
      />

      <!-- ===================================================
           INTERNAL TRANSFER MODAL
           =================================================== -->
      <StockInternalTransferModal
        ref="internalTransferModal"
        :active-venue-id="activeVenueId"
        @saved="onInternalTransferSaved"
      />
    </template>

    <!-- =====================================================
         MATERIALIZE FLOATING ACTION BUTTON

         Standard Materialize structure.

         Main button:
             +

         Child buttons:
             Delivery Note
             Internal Transfer
         ===================================================== -->
    <div v-if="activeVenueId" ref="stockFab" class="fixed-action-btn">
      <!-- ===================================================
           MAIN BUTTON
           =================================================== -->
      <a
        href="#!"
        class="btn-floating btn-large red darken-1"
        title="Add Stock Movement"
        aria-label="Add Stock Movement"
      >
        <i class="large material-icons"> mode_edit </i>
      </a>

      <!-- ===================================================
           CHILD ACTIONS

           Materialize handles all positioning and animation.
           =================================================== -->
      <ul>
        <!-- =================================================
             INTERNAL TRANSFER

             Appears highest because it is first in the list.
             ================================================= -->
        <li>
          <a
            href="#!"
            class="btn-floating purple accent-3 tooltipped"
            data-position="left"
            data-tooltip="Internal Transfer"
            title="Internal Transfer"
            aria-label="Internal Transfer"
            @click.prevent.stop="openInternalTransferModal"
          >
            <i class="material-icons"> swap_horiz </i>
          </a>
        </li>

        <!-- =================================================
             DELIVERY NOTE
             ================================================= -->
        <li>
          <a
            href="#!"
            class="btn-floating blue accent-2 tooltipped"
            data-position="left"
            data-tooltip="Delivery Note"
            title="Delivery Note"
            aria-label="Delivery Note"
            @click.prevent.stop="openDeliveryNoteModal"
          >
            <i class="material-icons"> local_shipping </i>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import {
  fetchStockDeliveryNotes,
  fetchStockInternalTransfers,
} from "@/scripts/sqlApiHelper/stockCountApi";

/* =========================================================
   TABLE COMPONENTS
   ========================================================= */

import StockDeliveryNotesTable from "@/components/stock/StockDeliveryNotesTable.vue";

import StockInternalTransfersTable from "@/components/stock/StockInternalTransfersTable.vue";

/* =========================================================
   MODAL COMPONENTS
   ========================================================= */

import StockDeliveryNoteModal from "@/components/stock/StockDeliveryNoteModal.vue";

import StockInternalTransferModal from "@/components/stock/StockInternalTransferModal.vue";

export default {
  name: "DeliveryNotesView",

  components: {
    StockDeliveryNotesTable,
    StockInternalTransfersTable,
    StockDeliveryNoteModal,
    StockInternalTransferModal,
  },

  /* =========================================================
     VENUE CONTEXT

     VenueSnapshot / useVenueContext remains the venue
     source of truth.
     ========================================================= */

  setup() {
    const { venueId } = useVenueContext();

    return {
      venueContextId: venueId,
    };
  },

  data() {
    return {
      /* -----------------------------------------------------
         DATA
         ----------------------------------------------------- */

      deliveryNotes: [],

      internalTransfers: [],

      /* -----------------------------------------------------
         PAGE STATE
         ----------------------------------------------------- */

      loading: false,

      error: null,

      /* -----------------------------------------------------
         MATERIALIZE INSTANCES
         ----------------------------------------------------- */

      fabInstance: null,

      tooltipInstances: [],
    };
  },

  computed: {
    /* =====================================================
       ACTIVE VENUE
       ===================================================== */

    activeVenueId() {
      return String(this.venueContextId ?? "").trim();
    },
  },

  watch: {
    /* =====================================================
       VENUE CHANGE

       When VenueSnapshot changes:

       1. destroy existing Materialize controls
       2. load venue data
       3. allow Vue to render
       4. initialise Materialize controls
       ===================================================== */

    activeVenueId: {
      immediate: true,

      async handler(newVenueId) {
        this.destroyMaterialize();

        await this.loadData();

        if (!newVenueId) {
          return;
        }

        await this.$nextTick();

        this.initMaterialize();
      },
    },
  },

  beforeUnmount() {
    this.destroyMaterialize();
  },

  methods: {
    /* =====================================================
       LOAD PAGE DATA
       ===================================================== */

    async loadData() {
      this.deliveryNotes = [];

      this.internalTransfers = [];

      this.error = null;

      if (!this.activeVenueId) {
        return;
      }

      this.loading = true;

      try {
        const [deliveryNotes, internalTransfers] = await Promise.all([
          fetchStockDeliveryNotes(this.activeVenueId),

          fetchStockInternalTransfers(this.activeVenueId),
        ]);

        this.deliveryNotes = Array.isArray(deliveryNotes) ? deliveryNotes : [];

        this.internalTransfers = Array.isArray(internalTransfers)
          ? internalTransfers
          : [];
      } catch (e) {
        console.error("[DELIVERY NOTES VIEW] Load failed:", e);

        this.error = e?.message || "Unable to load stock movements.";
      } finally {
        this.loading = false;
      }
    },

    /* =====================================================
       INITIALISE MATERIALIZE
       ===================================================== */

    initMaterialize() {
      this.initFloatingButton();

      this.initTooltips();
    },

    /* =====================================================
       MATERIALIZE FLOATING ACTION BUTTON

       This is the standard Materialize FAB behaviour shown
       in your example.

       It handles:

       - vertical positioning
       - animation
       - opacity
       - child button spacing
       - opening
       - closing
       ===================================================== */

    initFloatingButton() {
      if (!window.M || !window.M.FloatingActionButton || !this.$refs.stockFab) {
        console.warn(
          "[DELIVERY NOTES VIEW] Materialize FloatingActionButton is unavailable."
        );

        return;
      }

      this.fabInstance = window.M.FloatingActionButton.init(this.$refs.stockFab, {
        /* -------------------------------------------
                 Child buttons travel upwards.
                 ------------------------------------------- */

        direction: "top",

        /* -------------------------------------------
                 Click rather than hover.

                 Better for desktop + mobile/tablet.
                 ------------------------------------------- */

        hoverEnabled: false,

        /* -------------------------------------------
                 Standard circular FAB rather than toolbar
                 transformation.
                 ------------------------------------------- */

        toolbarEnabled: false,
      });
    },

    /* =====================================================
       MATERIALIZE TOOLTIPS

       These give the user the description when hovering:

       Delivery Note
       Internal Transfer

       They do NOT alter the FAB layout.
       ===================================================== */

    initTooltips() {
      if (!window.M || !window.M.Tooltip) {
        return;
      }

      const elements = this.$el?.querySelectorAll?.(".tooltipped");

      if (!elements || !elements.length) {
        return;
      }

      this.tooltipInstances = window.M.Tooltip.init(elements, {
        enterDelay: 200,

        exitDelay: 0,

        inDuration: 150,

        outDuration: 100,
      });
    },

    /* =====================================================
       DESTROY MATERIALIZE
       ===================================================== */

    destroyMaterialize() {
      /* ---------------------------------------------------
         FAB
         --------------------------------------------------- */

      if (this.fabInstance) {
        this.fabInstance.destroy?.();

        this.fabInstance = null;
      }

      /* ---------------------------------------------------
         TOOLTIPS
         --------------------------------------------------- */

      if (Array.isArray(this.tooltipInstances)) {
        this.tooltipInstances.forEach((instance) => {
          instance?.destroy?.();
        });
      }

      this.tooltipInstances = [];
    },

    /* =====================================================
       CLOSE FAB
       ===================================================== */

    closeFloatingButton() {
      this.fabInstance?.close?.();
    },

    /* =====================================================
       DELIVERY NOTE
       ===================================================== */

    openDeliveryNoteModal() {
      /* ---------------------------------------------------
         Close FAB first.
         --------------------------------------------------- */

      this.closeFloatingButton();

      /* ---------------------------------------------------
         Open Delivery Note modal.
         --------------------------------------------------- */

      this.$refs.deliveryNoteModal?.open?.();
    },

    /* =====================================================
       INTERNAL TRANSFER
       ===================================================== */

    openInternalTransferModal() {
      /* ---------------------------------------------------
         Close FAB first.
         --------------------------------------------------- */

      this.closeFloatingButton();

      /* ---------------------------------------------------
         Open Internal Transfer modal.
         --------------------------------------------------- */

      this.$refs.internalTransferModal?.open?.();
    },

    /* =====================================================
       DELIVERY NOTE SAVED
       ===================================================== */

    async onDeliveryNoteSaved() {
      await this.loadData();

      window.M?.toast?.({
        html: "Delivery Note saved",
      });
    },

    /* =====================================================
       INTERNAL TRANSFER SAVED
       ===================================================== */

    async onInternalTransferSaved() {
      await this.loadData();

      window.M?.toast?.({
        html: "Internal Transfer saved",
      });
    },
  },
};
</script>

<style scoped>
/* =========================================================
   PAGE
   ========================================================= */

.coredeck-page {
  width: 90%;
}

.page-header {
  margin-bottom: 18px;
}

/* =========================================================
   NO VENUE
   ========================================================= */

.no-venue-message {
  display: flex;

  align-items: center;

  gap: 9px;

  color: #795548;
}

/* =========================================================
   ERROR
   ========================================================= */

.error-card {
  border: 1px solid #ef9a9a;

  color: #c62828;
}

/* =========================================================
   MATERIALIZE FAB

   IMPORTANT:

   There is deliberately NO custom CSS applied to:

   .fixed-action-btn ul
   .fixed-action-btn ul li
   .fixed-action-btn .btn-floating

   Materialize owns all of that positioning.

   We only control where the complete FAB sits on screen.
   ========================================================= */

.fixed-action-btn {
  right: 28px;

  bottom: 28px;

  z-index: 1000;
}

/* =========================================================
   MOBILE
   ========================================================= */

@media only screen and (max-width: 600px) {
  .coredeck-page {
    width: 94%;
  }

  .fixed-action-btn {
    right: 18px;

    bottom: 18px;
  }
}
</style>
